/**
 * OperatorFormModal — Phase 24 / Task 24.3 (DEC-094).
 *
 * One modal for all three hauler-operator write paths:
 *   - mode 'platform' : confirm a platform-catalog operator picked from search
 *                       (catalog name read-only) + abbreviation + billing.
 *   - mode 'customer' : add a hauler-private customer not in the catalog
 *                       (name editable) + abbreviation + billing.
 *   - mode 'edit'     : edit abbreviation + billing on an existing junction row.
 *
 * operatorId / isPlatformOperator are immutable post-create, so 'edit' only
 * exposes abbreviation + billing contacts (matches the PATCH endpoint).
 *
 * 0202 (Corbin 2026-10-17): a catalog operator shows Paelode's display name, with the
 * official name underneath. When Paelode has an abbreviation for it, tickets carry that
 * code, so it is shown read-only ("Tickets use DVN — set by Paelode") and the hauler's own
 * code is not asked for. The input appears for a private customer, for a catalog operator
 * Paelode has no code for yet (tickets fall back to the hauler's), and when Paelode's code
 * clashes with another of this hauler's customers on create.
 */

import * as React from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { fieldStyle, labelStyle, FormRow } from './form-bits';
import {
  CustomerRatesFields,
  EMPTY_RATES,
  parseRatesDraft,
  ratesDraftFrom,
  type RatesDraft,
} from './customer-rates';
import { Pill } from './ui-bits';
import { DisposalExclusionsSection } from './DisposalExclusionsSection';
import { JobTypePricesSection } from './JobTypePricesSection';
import { MANAGE_CUSTOMER_PRICES_ROLES } from '../../lib/role-gates';
import { useUserProfile } from '../../lib/use-user-timezone';
import {
  useCreateHaulerOperator,
  useUpdateHaulerOperator,
  type HaulerOperator,
  type PlatformOperatorResult,
} from './hooks';
import { ABBREVIATION_RE, ABBREVIATION_RULE_TEXT } from '@lodex-pro/shared/abbreviation';

export type OperatorFormMode = 'platform' | 'customer' | 'edit';

export interface OperatorFormModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly mode: OperatorFormMode;
  readonly platformOperator?: PlatformOperatorResult | null;
  readonly editing?: HaulerOperator | null;
  /**
   * OPEN-201 (c) — 'platform' mode opened after a one-click Add was refused because Paelode's
   * code is already used by another of this hauler's customers: ask for a code straight away.
   */
  readonly initialCodeTaken?: boolean;
}

function suggestAbbrev(name: string): string {
  const letters = name.replace(/[^A-Za-z]/g, '');
  return letters.slice(0, 3).toUpperCase();
}

export function OperatorFormModal({
  open,
  onOpenChange,
  mode,
  platformOperator,
  editing,
  initialCodeTaken = false,
}: OperatorFormModalProps): React.JSX.Element {
  const [customerName, setCustomerName] = React.useState('');
  const [abbrev, setAbbrev] = React.useState('');
  const [contactName, setContactName] = React.useState('');
  const [contactEmail, setContactEmail] = React.useState('');
  const [contactPhone, setContactPhone] = React.useState('');
  const [address, setAddress] = React.useState('');
  const [active, setActive] = React.useState(true);
  // DEC-198 rates + migration-0086 clock; the rules live in customer-rates.tsx.
  const [rates, setRates] = React.useState<RatesDraft>(EMPTY_RATES);
  const [error, setError] = React.useState<string | null>(null);
  // gap 1 (D-10): who gets the "Edit in Billing" link on the read-only job-type prices.
  const { data: profileData } = useUserProfile();
  const canEditPricesInBilling = MANAGE_CUSTOMER_PRICES_ROLES.has(profileData?.profile.role ?? '');
  // Paelode's code clashed with another customer of this hauler on create: ask for one.
  const [platformCodeTaken, setPlatformCodeTaken] = React.useState(false);

  // The code Paelode controls for this operator, when it has one (tickets use it).
  const paelodeAbbrev =
    mode === 'platform'
      ? (platformOperator?.abbreviation ?? null)
      : mode === 'edit' && editing?.isPlatformOperator === true
        ? editing.platformAbbreviation
        : null;
  const askAbbrev = paelodeAbbrev === null || (mode === 'platform' && platformCodeTaken);
  // The official regulator name, shown under Paelode's display name when they differ.
  const officialName =
    mode === 'platform'
      ? (platformOperator?.name ?? null)
      : mode === 'edit'
        ? (editing?.catalogName ?? null)
        : null;

  React.useEffect(() => {
    if (!open) return;
    setError(null);
    setPlatformCodeTaken(mode === 'platform' && initialCodeTaken);
    if (mode === 'edit' && editing != null) {
      // The name haulers see (customer name, else Paelode's display name, else official).
      setCustomerName(editing.resolvedName);
      setAbbrev(editing.customerAbbreviation);
      setContactName(editing.billingContactName ?? '');
      setContactEmail(editing.billingContactEmail ?? '');
      setContactPhone(editing.billingContactPhone ?? '');
      setAddress(editing.billingAddress ?? '');
      setActive(editing.isActive);
      setRates(ratesDraftFrom(editing));
    } else if (mode === 'platform' && platformOperator != null) {
      setCustomerName(platformOperator.displayName ?? platformOperator.name);
      setAbbrev(
        initialCodeTaken
          ? ''
          : suggestAbbrev(platformOperator.displayName ?? platformOperator.name),
      );
      if (initialCodeTaken) {
        setError(
          `You already use ${platformOperator.abbreviation ?? 'that code'} for another customer. Enter a code of your own for this one; tickets still use ${platformOperator.abbreviation ?? "Paelode's code"}.`,
        );
      }
      setContactName('');
      setContactEmail('');
      setContactPhone('');
      setAddress('');
      setRates(EMPTY_RATES);
    } else {
      setCustomerName('');
      setAbbrev('');
      setContactName('');
      setContactEmail('');
      setContactPhone('');
      setAddress('');
      setRates(EMPTY_RATES);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, editing?.id, platformOperator?.id, initialCodeTaken]);

  const close = (): void => onOpenChange(false);
  const createMutation = useCreateHaulerOperator(() => {
    toast.success('Operator added');
    close();
  });
  const updateMutation = useUpdateHaulerOperator(editing?.id ?? '');
  const pending = createMutation.isPending || updateMutation.isPending;

  const handleSave = React.useCallback((): void => {
    if (askAbbrev && !ABBREVIATION_RE.test(abbrev.trim())) {
      setError(ABBREVIATION_RULE_TEXT);
      return;
    }
    if (mode === 'customer' && customerName.trim() === '') {
      setError('Customer name is required.');
      return;
    }
    const parsed = parseRatesDraft(rates);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    const r = parsed.rates;
    setError(null);
    // Conditional spread (exactOptionalPropertyTypes) — omit empty billing fields
    // on create; on edit send null to explicitly clear a field.
    const billing = {
      ...(contactName.trim() !== '' && { billingContactName: contactName.trim() }),
      ...(contactEmail.trim() !== '' && { billingContactEmail: contactEmail.trim() }),
      ...(contactPhone.trim() !== '' && { billingContactPhone: contactPhone.trim() }),
      ...(address.trim() !== '' && { billingAddress: address.trim() }),
    };
    // Pricing: on create omit an unset rate entirely; on edit send null so
    // clearing the box actually clears the stored rate.
    const pricing = {
      ...(r.perBblPriceCents !== null && { perBblPriceCents: r.perBblPriceCents }),
      ...(r.hourlyPriceCents !== null && { hourlyPriceCents: r.hourlyPriceCents }),
      ...(r.bidPriceCents !== null && { bidPriceCents: r.bidPriceCents }),
      // Migration 0086 — same omit-on-create shape as the rates above.
      ...(r.billingStart !== null && { billingStart: r.billingStart }),
      ...(r.billingEnd !== null && { billingEnd: r.billingEnd }),
    };
    const onError = (err: unknown): void => {
      // 0205 — another customer of this company already prints that code on its tickets.
      const code =
        typeof err === 'object' && err !== null ? (err as { code?: unknown }).code : null;
      if (code === 'TICKET_PREFIX_TAKEN') {
        setError(
          mode === 'platform' && !askAbbrev
            ? `Another of your customers already prints ${paelodeAbbrev ?? "Paelode's code"} on its tickets. Change that customer's code first, then add this one.`
            : 'Another of your customers already prints that code on its tickets. Choose a different code.',
        );
        return;
      }
      // The only 400 a catalog create with Paelode's code can hit is that code being taken
      // by another of this hauler's customers: reveal the field and explain.
      if (
        mode === 'platform' &&
        !askAbbrev &&
        typeof err === 'object' &&
        err !== null &&
        (err as { status?: unknown }).status === 400
      ) {
        setPlatformCodeTaken(true);
        setAbbrev('');
        setError(
          `You already use ${paelodeAbbrev ?? 'that code'} for another customer. Enter a code of your own for this one; tickets still use ${paelodeAbbrev ?? "Paelode's code"}.`,
        );
        return;
      }
      setError('Could not save. Check your inputs and try again.');
    };

    if (mode === 'edit') {
      updateMutation.mutate(
        {
          // Not sent while Paelode's code applies (the API ignores it then anyway).
          ...(askAbbrev && { customerAbbreviation: abbrev.trim().toUpperCase() }),
          billingContactName: contactName.trim() === '' ? null : contactName.trim(),
          billingContactEmail: contactEmail.trim() === '' ? null : contactEmail.trim(),
          billingContactPhone: contactPhone.trim() === '' ? null : contactPhone.trim(),
          billingAddress: address.trim() === '' ? null : address.trim(),
          isActive: active,
          // On edit every rate is sent: null clears the customer value (rate or clock
          // policy) so the operator/job-type/tenant default applies again.
          ...r,
        },
        {
          onSuccess: () => {
            toast.success('Operator updated');
            close();
          },
          onError,
        },
      );
      return;
    }
    createMutation.mutate(
      mode === 'platform'
        ? {
            ...(platformOperator?.id !== undefined && { operatorId: platformOperator.id }),
            // Omitted while Paelode's code applies: the API stores Paelode's.
            ...(askAbbrev && { customerAbbreviation: abbrev.trim().toUpperCase() }),
            isPlatformOperator: true,
            ...billing,
            ...pricing,
          }
        : {
            customerName: customerName.trim(),
            customerAbbreviation: abbrev.trim().toUpperCase(),
            isPlatformOperator: false,
            ...billing,
            ...pricing,
          },
      { onError },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mode,
    abbrev,
    customerName,
    contactName,
    contactEmail,
    contactPhone,
    address,
    active,
    // `rates` carries the clock selects too. They used to be missing from this list,
    // so changing only the clock and saving sent the previous value.
    rates,
    platformOperator,
    askAbbrev,
    paelodeAbbrev,
  ]);

  const title =
    mode === 'edit'
      ? 'Edit operator'
      : mode === 'platform'
        ? 'Add operator'
        : 'Add customer (not in catalog)';
  const nameReadOnly = mode === 'platform' || mode === 'edit';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <FormRow
            label={mode === 'customer' ? 'Customer name' : 'Operator'}
            htmlFor="ho-name"
            required
          >
            <input
              id="ho-name"
              value={customerName}
              readOnly={nameReadOnly}
              onChange={(e): void => setCustomerName(e.target.value)}
              placeholder="e.g. Cimarron Ridge Operating"
              style={{ ...fieldStyle, opacity: nameReadOnly ? 0.7 : 1 }}
            />
          </FormRow>
          {officialName !== null && officialName !== customerName && (
            <p style={{ ...labelStyle, textTransform: 'none' }} data-testid="ho-official-name">
              Official name: {officialName}
            </p>
          )}
          {mode === 'platform' && platformOperator?.regulatorOperatorId != null && (
            <p style={{ ...labelStyle, textTransform: 'none' }}>
              OCC operator ID {platformOperator.regulatorOperatorId}
              {platformOperator.state != null ? ` · ${platformOperator.state}` : ''}
            </p>
          )}
          {/* Task 24.6 — platform producer/disposal flags (read-only). */}
          {(() => {
            const isProducer =
              mode === 'platform' ? platformOperator?.isProducer : (editing?.isProducer ?? null);
            const isDisposal =
              mode === 'platform' ? platformOperator?.isDisposal : (editing?.isDisposal ?? null);
            if (isProducer == null && isDisposal == null) return null;
            return (
              <div style={{ display: 'flex', gap: '6px' }}>
                {isProducer === true && <Pill label="Producer" tone="accent" />}
                {isDisposal === true && <Pill label="Disposal" tone="accent" />}
                {isProducer === false && isDisposal === false && (
                  <span style={{ ...labelStyle, textTransform: 'none' }}>
                    Not flagged producer or disposal
                  </span>
                )}
              </div>
            );
          })()}
          {paelodeAbbrev !== null && (
            <p style={{ ...labelStyle, textTransform: 'none' }} data-testid="ho-paelode-abbrev">
              Tickets use {paelodeAbbrev} — set by Paelode
            </p>
          )}
          {askAbbrev && (
            <FormRow
              label={
                paelodeAbbrev !== null
                  ? 'Your code for this customer (letters only, 2–4)'
                  : 'Abbreviation (letters only, 2–4)'
              }
              htmlFor="ho-abbrev"
              required
            >
              <input
                id="ho-abbrev"
                value={abbrev}
                maxLength={4}
                onChange={(e): void => setAbbrev(e.target.value.toUpperCase())}
                placeholder="DVN"
                style={{ ...fieldStyle, textTransform: 'uppercase', maxWidth: '120px' }}
              />
            </FormRow>
          )}
          {askAbbrev && paelodeAbbrev === null && mode !== 'customer' && (
            <p style={{ ...labelStyle, textTransform: 'none' }}>
              Paelode has not set a code for this operator yet, so tickets use yours.
            </p>
          )}
          {/* DEC-198 / DEC-383 — the customer's general rate per method: used for any job
              type without its own price for this customer (set in Billing → Customer prices);
              then the job type's standard rate. A ticket whose rate resolves nowhere cannot be
              approved into billing. */}
          <div
            style={{
              borderTop: '1px solid var(--color-border-secondary)',
              paddingTop: '10px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <CustomerRatesFields value={rates} onChange={setRates} />
          </div>
          <FormRow label="Billing contact name" htmlFor="ho-contact">
            <input
              id="ho-contact"
              value={contactName}
              onChange={(e): void => setContactName(e.target.value)}
              style={fieldStyle}
            />
          </FormRow>
          <FormRow label="Billing contact email" htmlFor="ho-email">
            <input
              id="ho-email"
              type="email"
              value={contactEmail}
              onChange={(e): void => setContactEmail(e.target.value)}
              style={fieldStyle}
            />
          </FormRow>
          <FormRow label="Billing contact phone" htmlFor="ho-phone">
            <input
              id="ho-phone"
              value={contactPhone}
              onChange={(e): void => setContactPhone(e.target.value)}
              placeholder="+1 580 555 0100"
              style={fieldStyle}
            />
          </FormRow>
          <FormRow label="Billing address" htmlFor="ho-address">
            <input
              id="ho-address"
              value={address}
              onChange={(e): void => setAddress(e.target.value)}
              style={fieldStyle}
            />
          </FormRow>
          {/* Task 24.6 — is-active toggle (edit mode; DISPATCH_MANAGER+). */}
          {mode === 'edit' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={active}
                onChange={(e): void => setActive(e.target.checked)}
              />
              <span style={{ ...labelStyle, marginBottom: 0, textTransform: 'none' }}>
                Active (uncheck to deactivate this operator)
              </span>
            </label>
          )}
          {/* Phase 28 (DEC-331 D-5) — this customer's "never send here" list. Saves on its
              own, so it exists only for a customer that already exists. */}
          {/* gap 1 (DEC-383, D-10) — this customer's job-type prices, read-only here; they
              are edited in web-billing. */}
          {mode === 'edit' && editing != null && (
            <JobTypePricesSection
              haulerOperatorId={editing.id}
              canEditInBilling={canEditPricesInBilling}
            />
          )}
          {mode === 'edit' && editing != null && (
            <DisposalExclusionsSection haulerOperatorId={editing.id} />
          )}
          {error !== null && (
            <p role="alert" style={{ ...labelStyle, color: 'var(--color-danger)' }}>
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={pending}
            onClick={handleSave}
            style={{
              backgroundColor: 'var(--color-accent-blue)',
              color: 'var(--color-on-accent)',
              borderColor: 'var(--color-accent-blue)',
            }}
          >
            {pending ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Add operator'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
